class DetailsModal extends HTMLElement {
	constructor() {
		super();
		this.header = document.querySelector(".header-wrapper");
		this.detailsContainer = this.querySelector("details");
		this.summaryToggle = this.querySelector("summary");
		this.headerSearchFocusObserver = null;
		this.headerSearchFocusListener = null;
		this.headerSearchFocusTimeout = null;
		this.compactSearchCloseTimeouts = [];
		this.header.preventHide = false;

		this.detailsContainer.addEventListener(
			"keyup",
			(event) => event.code === "Escape" && this.close(),
		);
		this.summaryToggle.addEventListener(
			"click",
			this.onSummaryClick.bind(this),
		);
		this.addEventListener("pointerdown", this.onCompactSearchClose.bind(this), true);
		this.addEventListener("click", this.onCompactSearchClose.bind(this), true);
		this.querySelectorAll('button[type="button"]').forEach((button) => {
			button.addEventListener("click", this.close.bind(this));
		});

		this.summaryToggle.setAttribute("role", "button");
	}

	isOpen() {
		return this.detailsContainer.hasAttribute("open");
	}

	onSummaryClick(event) {
		event.preventDefault();
		if (event.target.closest("details").hasAttribute("open")) {
			this.close();
			this.header.preventHide = false;
		} else {
			this.open(event);
			if (!this.header) return;
			this.header.preventHide =
				this.detailsContainer.open && !this.usesCompactHeaderSearch();
		}
	}

	onBodyClick(event) {
		if (
			this.usesCompactHeaderSearch() &&
			event.target.closest(".aa-Panel")
		)
			return;

		if (
			!this.contains(event.target) ||
			event.target.classList.contains("modal-overlay")
		)
			this.close(false);
	}

	open(event) {
		this.onBodyClickEvent =
			this.onBodyClickEvent || this.onBodyClick.bind(this);
		event.target.closest("details").setAttribute("open", true);
		document.body.addEventListener("click", this.onBodyClickEvent);

		if (this.usesCompactHeaderSearch()) {
			this.closeHeaderMegaMenus();
			document.body.classList.add("geometry-algolia-search-only");
			document.body.classList.remove("overflow-hidden");
			this.header.preventHide = false;
			this.setHeaderSearchModalState(false);
			this.startHeaderSearchFocusHandoff();
			return;
		}

		document.body.classList.add("overflow-hidden");

		trapFocus(
			this.getFocusTrapContainer(),
			this.getInitialFocusElement(),
		);

		this.scheduleHeaderSearchInputFocus();
	}

	usesCompactHeaderSearch() {
		return (
			this.classList.contains("header__search") &&
			Boolean(
				this.querySelector("[data-algolia-search-form]") ||
					this.querySelector("[data-algolia-search-input]") ||
					document.querySelector(".aa-Autocomplete")
			)
		);
	}

	closeHeaderMegaMenus() {
		this.header
			.querySelectorAll(".list-menu--megamenu-visible")
			.forEach((menu) => menu.classList.remove("list-menu--megamenu-visible"));
	}

	setHeaderSearchModalState(isModal) {
		const dialog = this.detailsContainer.querySelector('[role="dialog"]');
		if (!dialog) return;

		if (isModal) {
			dialog.setAttribute("aria-modal", "true");
		} else {
			dialog.setAttribute("aria-modal", "false");
		}
	}

	getFocusTrapContainer() {
		return (
			this.detailsContainer.querySelector('[tabindex="-1"]') ||
			this.detailsContainer.querySelector('[role="dialog"]') ||
			this.detailsContainer
		);
	}

	getInitialFocusElement() {
		if (this.classList.contains("header__search")) {
			return (
				this.getHeaderSearchInput() ||
				this.detailsContainer.querySelector('input:not([type="hidden"])')
			);
		}

		return this.detailsContainer.querySelector('input:not([type="hidden"])');
	}

	focusHeaderSearchInput() {
		if (!this.classList.contains("header__search")) return;
		if (!this.isOpen()) return false;

		const searchInput = this.getHeaderSearchInput();
		if (!searchInput) return false;

		if (document.activeElement !== searchInput) {
			searchInput.focus({ preventScroll: true });
		}

		this.updateHeaderSearchDisplayMode();
		return document.activeElement === searchInput;
	}

	scheduleHeaderSearchInputFocus(stopOnSuccess = false) {
		window.requestAnimationFrame(() => {
			const focused = this.focusHeaderSearchInput();
			if (focused && stopOnSuccess) this.stopHeaderSearchFocusHandoff();
		});
	}

	startHeaderSearchFocusHandoff() {
		this.stopHeaderSearchFocusHandoff();

		this.focusHeaderSearchInput();
		this.scheduleHeaderSearchInputFocus();

		// Algolia builds/replaces autocomplete DOM asynchronously on first open.
		this.headerSearchFocusObserver = new MutationObserver(() => {
			this.focusHeaderSearchInput();
		});

		this.headerSearchFocusObserver.observe(document.body, {
			attributes: true,
			attributeFilter: ["class", "style", "hidden", "aria-hidden"],
			childList: true,
			subtree: true,
		});

		this.headerSearchFocusListener = (event) => {
			if (!this.shouldRestoreHeaderSearchFocus(event.target)) return;
			this.scheduleHeaderSearchInputFocus();
		};
		document.addEventListener("focusin", this.headerSearchFocusListener, true);

		this.headerSearchFocusTimeout = window.setTimeout(() => {
			this.stopHeaderSearchFocusHandoff();
		}, 4000);
	}

	stopHeaderSearchFocusHandoff() {
		if (this.headerSearchFocusObserver) {
			this.headerSearchFocusObserver.disconnect();
			this.headerSearchFocusObserver = null;
		}

		if (this.headerSearchFocusListener) {
			document.removeEventListener("focusin", this.headerSearchFocusListener, true);
			this.headerSearchFocusListener = null;
		}

		if (this.headerSearchFocusTimeout) {
			window.clearTimeout(this.headerSearchFocusTimeout);
			this.headerSearchFocusTimeout = null;
		}
	}

	shouldRestoreHeaderSearchFocus(target) {
		if (!target || !this.usesCompactHeaderSearch() || !this.isOpen()) return false;
		if (this.contains(target)) return false;
		if (target.closest && target.closest(".aa-Panel")) return false;

		return (
			target === document.body ||
			target === document.documentElement ||
			target.id === "INDblindNotif" ||
			(target.classList && target.classList.contains("INDhiddenText"))
		);
	}

	getHeaderSearchInput(includeFallback = true) {
		const selectors = [
			".aa-Input",
			"[data-algolia-search-input]",
			'[id^="autocomplete-"][id$="-input"]',
		];

		if (includeFallback) selectors.push('.search__input[type="search"]');

		for (const selector of selectors) {
			const searchInput = this.detailsContainer.querySelector(selector);

			if (this.isFocusableHeaderSearchInput(searchInput)) return searchInput;
		}
	}

	isFocusableHeaderSearchInput(searchInput) {
		if (!searchInput || searchInput.disabled) return false;
		if (searchInput.type === "hidden") return false;
		if (!searchInput.getClientRects().length) return false;

		const style = window.getComputedStyle(searchInput);
		return style.visibility !== "hidden" && style.display !== "none";
	}

	updateHeaderSearchDisplayMode() {
		if (!this.classList.contains("header__search")) return;

		const algoliaInput = this.getHeaderSearchInput(false);
		document.body.classList.toggle(
			"geometry-algolia-search-only",
			Boolean(
				this.usesCompactHeaderSearch() ||
					(algoliaInput && !this.detailsContainer.contains(algoliaInput)),
			),
		);
	}

	onCompactSearchClose(event) {
		if (!this.isCompactSearchCloseControl(event.target)) return;

		event.preventDefault();
		event.stopImmediatePropagation();
		this.closeCompactHeaderSearch();
	}

	isCompactSearchCloseControl(target) {
		return (
			this.isOpen() &&
			this.usesCompactHeaderSearch() &&
			Boolean(
				target.closest(".aa-ClearButton") ||
					target.closest(".geometry-algolia-search-close"),
			)
		);
	}

	closeCompactHeaderSearch() {
		this.clearHeaderSearchInputs();
		this.close(false);

		this.scheduleAutocompletePanelClose();
	}

	clearHeaderSearchInputs() {
		this.detailsContainer
			.querySelectorAll('.aa-Input, [data-algolia-search-input], .search__input[type="search"]')
			.forEach((input) => {
				if (input.value === "") return;
				input.value = "";
				input.dispatchEvent(new Event("input", { bubbles: true }));
			});
	}

	closeAutocompletePanel() {
		document.querySelectorAll(".aa-Panel").forEach((panel) => panel.remove());
		document
			.querySelectorAll('.aa-Autocomplete[aria-expanded="true"], .aa-Input[aria-expanded="true"], [id^="autocomplete-"][id$="-input"][aria-expanded="true"]')
			.forEach((input) => {
				input.setAttribute("aria-expanded", "false");
			});
	}

	scheduleAutocompletePanelClose() {
		this.clearCompactSearchCloseTimeouts();
		[0, 50, 150].forEach((delay) => {
			const timeout = window.setTimeout(() => {
				this.closeAutocompletePanel();
			}, delay);

			this.compactSearchCloseTimeouts.push(timeout);
		});
	}

	clearCompactSearchCloseTimeouts() {
		this.compactSearchCloseTimeouts.forEach((timeout) => {
			window.clearTimeout(timeout);
		});
		this.compactSearchCloseTimeouts = [];
	}

	close(focusToggle = true) {
		this.stopHeaderSearchFocusHandoff();
		this.clearCompactSearchCloseTimeouts();

		const activeElement = document.activeElement;
		if (
			activeElement &&
			this.classList.contains("header__search") &&
			(this.contains(activeElement) || activeElement.closest(".aa-Autocomplete"))
		) {
			activeElement.blur();
		}

		removeTrapFocus(focusToggle ? this.summaryToggle : null);
		this.detailsContainer.removeAttribute("open");
		document.body.removeEventListener("click", this.onBodyClickEvent);
		document.body.classList.remove("overflow-hidden");
		document.body.classList.remove("geometry-algolia-search-only");
		this.closeAutocompletePanel();
		window.requestAnimationFrame(() => {
			this.closeAutocompletePanel();
		});
		this.setHeaderSearchModalState(true);
		this.header.preventHide = false;
	}
}

customElements.define("details-modal", DetailsModal);
